import SearchHotelWithinRadiusResult from '@app/model/flexi_sets/SearchHotelWithinRadiusResult';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Tag from '@app/component/tag/Tag';

const FlexiSetAreaHotelsListingFragment: React.FunctionComponent<{
	areaHotels: SearchHotelWithinRadiusResult[],
	selectedIds: Set<number>,
	onToggle: (hotel: SearchHotelWithinRadiusResult, checked: boolean) => void,
	onClose: () => void
}> = ({ areaHotels, selectedIds, onToggle, onClose }) => {

	const containerRef = useRef<HTMLDivElement | null>(null);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	const isEmpty = _isEmpty(areaHotels);

	// Paging config/state
	const PAGE_SIZE = 20;
	const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
	const [isIntersecting, setIsIntersecting] = useState<boolean>(false);

	// Reset paging when areaHotels changes
	useEffect(() => {
		setVisibleCount(PAGE_SIZE);
	}, [areaHotels]);

	// Items to show for current page
	const visibleHotels = useMemo(() => areaHotels.slice(0, Math.min(visibleCount, areaHotels.length)), [areaHotels, visibleCount]);
	const hasMore = visibleCount < areaHotels.length;

	// Select All control state (for visible items only)
	const selectAllRef = useRef<HTMLInputElement | null>(null);
	const visibleSelectedCount = useMemo(() => visibleHotels.reduce((acc, h) => acc + (selectedIds.has(h.id) ? 1 : 0), 0), [visibleHotels, selectedIds]);
	const allVisibleSelected = visibleHotels.length > 0 && visibleSelectedCount === visibleHotels.length;
	const noneVisibleSelected = visibleSelectedCount === 0;
	useEffect(() => {
		if (selectAllRef.current) {
			selectAllRef.current.indeterminate = !noneVisibleSelected && !allVisibleSelected;
		}
	}, [noneVisibleSelected, allVisibleSelected]);

	const onToggleSelectAll = (checked: boolean) => {
		if (checked) {
			// add all visible not yet selected
			visibleHotels.forEach(hotel => {
				if (!selectedIds.has(hotel.id)) onToggle(hotel, true);
			});
		} else {
			// remove all visible currently selected
			visibleHotels.forEach(hotel => {
				if (selectedIds.has(hotel.id)) onToggle(hotel, false);
			});
		}
	};

	// Intersection observer to load more when sentinel becomes visible
	useEffect(() => {
		const rootEl = containerRef.current;
		const targetEl = sentinelRef.current;
		if (!rootEl || !targetEl) return;
		if (!hasMore) return; // nothing more to load

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry.isIntersecting) {
					setIsIntersecting(true);
					requestAnimationFrame(() => {
						setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, areaHotels.length));
						setIsIntersecting(false);
					});
				}
			},
			{
				root: rootEl,
				rootMargin: '0px 0px 200px 0px',
				threshold: 0.1,
			},
		);

		observer.observe(targetEl);
		return () => observer.disconnect();
	}, [visibleCount, hasMore, areaHotels.length]);

	// Memoized row item to avoid rerenders when unrelated rows change
	const HotelRow = React.memo(({ hotel, index, checked }: {
		hotel: SearchHotelWithinRadiusResult,
		index: number,
		checked: boolean
	}) => {
		const checkboxId = `select_${hotel.id}`;
		return (
			<div className="wrapper pt-4 px-5 space-y-1">
				<div className="flex items-start justify-between gap-x-2">
					<div className="flex items-start gap-x-2">
						<input
							type="checkbox"
							id={checkboxId}
							className="input-checkbox"
							checked={checked}
							onChange={(e) => onToggle(hotel, e.target.checked)}
						/>
						<div className="flex flex-col items-start">
							<label htmlFor={checkboxId}
										 className="text-xs font-medium text-gray-900 truncate max-w-[200px] hover:cursor-pointer">{index + 1}. {hotel.name}</label>
							<span
								className="block text-xs font-light text-gray-400 truncate max-w-[200px]">{hotel.location.city}, {hotel.location.country?.name}</span>
						</div>
					</div>
					<img className="h-9 w-9 rounded-full" src={hotel.imageUrl} alt={hotel.name} />
				</div>
				<div className="flex items-center justify-between gap-x-2">
					<div className="flex flex-wrap justify-start gap-x-1 gap-y-1">
						<Tag state="gray" extraClassName="!text-[9px] !font-medium !text-gray-400">{hotel.type}</Tag>
						<Tag state="gray"
								 extraClassName="!text-[9px] !font-medium !text-gray-400">{hotel.rooms[0].numberOfBedrooms} BD</Tag>
						<Tag state="gray"
								 extraClassName="!text-[9px] !font-medium !text-gray-400">{hotel.rooms[0].numberOfBathrooms} BA</Tag>
						<Tag state="gray"
								 extraClassName="!text-[9px] !font-medium !text-gray-400">{hotel.rooms[0].numberOfBeds} Beds</Tag>
					</div>
					<a href={hotel.hotelUrl} target="_blank"
						 className="text-xs font-semibold text-gray-900 hover:text-blue-500 focus:outline-none">View
						<span className="ml-1">→</span>
					</a>
				</div>
			</div>
		);
	}, (prev, next) => prev.checked === next.checked && prev.index === next.index && prev.hotel.id === next.hotel.id);

	return <div className="md:w-[325px] relative rounded-lg bg-white size-full shadow pt-8 pb-2">

		<div className="space-y-4 divide-y divide-gray-200">
			<div className="wrapper space-y-2 px-5">
				<header className="relative flex justify-between items-center mb-1.5">
					<p className="text-base font-semibold text-gray-900">{areaHotels.length} Listings</p>
					<a onClick={onClose}><XMarkIcon
						className="h-4 w-4 flex-none text-gray-900 hover:text-blue-500 hover:cursor-pointer" /></a>
				</header>
				<div className="flex items-center gap-x-2">
					<input
						ref={selectAllRef}
						type="checkbox"
						id="select_all"
						name="checkbox9"
						className="input-checkbox"
						disabled={isEmpty}
						checked={!isEmpty && allVisibleSelected}
						onChange={(e) => onToggleSelectAll(e.target.checked)}
					/>
					<label htmlFor="select_all"
								 className={`block text-sm font-normal leading-6 ${isEmpty ? 'text-gray-400' : 'text-gray-700'}`}>
						Select all
					</label>
				</div>
			</div>
			{!isEmpty && <div className="max-h-[300px] md:max-h-[calc(100vh_-_412px)] overflow-auto"
												ref={containerRef}>
				<div className="space-y-4 divide-y divide-gray-200">
					{visibleHotels.map((hotel, index) => (
						<HotelRow
							key={hotel.id}
							hotel={hotel}
							index={index}
							checked={selectedIds.has(hotel.id)}
						/>
					))}
				</div>

				{/* Sentinel for infinite scroll */}
				{hasMore && <>
					<div ref={sentinelRef} className="h-6" />
					<div className="wrapper px-5 pb-4 -mt-4 text-center border-t border-gray-200">
						<p className="font-medium text-xs text-gray-400 mt-2">{isIntersecting ? 'Loading…' : 'Scroll to load more'}</p>
					</div>
				</>}
			</div>}
		</div>
	</div>;

};

export default FlexiSetAreaHotelsListingFragment;